/**
 * Righe di classifica normalizzate — un solo formato per tutte le viste.
 *
 * Andrea 2026-09-09: la classifica serve in tre forme diverse
 *   1. campionato → la calcola il backend (view v_classifica_live)
 *   2. gironi CL  → va calcolata per girone: la view aggrega TUTTA la
 *      competizione, quindi con la fase a eliminazione in corso
 *      sporcherebbe la classifica dei gironi
 *   3. a stagione appena aperta → tutte le squadre a zero, non "vuota":
 *      la view non ha righe finché non c'è almeno una partita calcolata
 *
 * Qui si normalizzano tutte e tre in `RigaClassifica`, cosi' Home e Lega
 * disegnano una tabella sola.
 */

import type { ClassificaRow, PartitaItem } from "../../hooks/useFase5";

export interface RigaClassifica {
  squadra_id: string;
  posizione: number;
  punti: number;
  fantapunti: number;
  /** Fantapunti fatti dalle avversarie (Andrea 2026-09-17). */
  fantapunti_subiti: number;
  gol_fatti: number;
  gol_subiti: number;
  diff_reti: number;
  partite_giocate: number;
  vinte: number;
  pareggiate: number;
  perse: number;
  /** false = squadra ancora senza partite calcolate (riga tutta a zero). */
  giocato: boolean;
}

function rigaVuota(squadraId: string, posizione: number): RigaClassifica {
  return {
    squadra_id: squadraId,
    posizione,
    punti: 0,
    fantapunti: 0,
    fantapunti_subiti: 0,
    gol_fatti: 0,
    gol_subiti: 0,
    diff_reti: 0,
    partite_giocate: 0,
    vinte: 0,
    pareggiate: 0,
    perse: 0,
    giocato: false,
  };
}

/** Ordine dei gironi CL — cascata del regolamento §18, la stessa che il
 * backend usa per decidere le qualificate (_classifica_girone): punti,
 * fantapunti totali, differenza reti, gol fatti. Ultimo criterio il nome,
 * per avere un ordine stabile e leggibile quando sono tutte a zero.
 *
 * NB: la view del campionato dal 2026-09-24 ordina punti, fantapunti,
 * diff reti, MENO fantapunti subiti, nome (Andrea); qui resta la cascata
 * del regolamento per i gironi. */
function ordina(
  righe: RigaClassifica[],
  nomeSquadra: (id: string) => string,
): RigaClassifica[] {
  return [...righe]
    .sort(
      (a, b) =>
        b.punti - a.punti ||
        b.fantapunti - a.fantapunti ||
        b.diff_reti - a.diff_reti ||
        b.gol_fatti - a.gol_fatti ||
        nomeSquadra(a.squadra_id).localeCompare(nomeSquadra(b.squadra_id), "it"),
    )
    .map((r, i) => ({ ...r, posizione: i + 1 }));
}

/** Classifica del backend + le squadre che non hanno ancora giocato (in
 * coda, a zero). Con la view vuota escono tutte le squadre a zero. */
export function classificaConTutteLeSquadre(
  righe: ClassificaRow[] | undefined,
  squadreIds: string[],
  nomeSquadra: (id: string) => string,
): RigaClassifica[] {
  const conPartite = new Map<string, RigaClassifica>();
  for (const r of righe ?? []) {
    conPartite.set(r.squadra_id, {
      squadra_id: r.squadra_id,
      posizione: r.posizione,
      punti: r.punti,
      fantapunti: Number(r.fantapunti_totali),
      fantapunti_subiti: Number(r.fantapunti_subiti ?? 0),
      gol_fatti: r.gol_fantacalcio_fatti,
      gol_subiti: r.gol_fantacalcio_subiti,
      diff_reti: r.diff_reti,
      partite_giocate: r.partite_giocate,
      vinte: r.vinte,
      pareggiate: r.pareggiate,
      perse: r.perse,
      giocato: true,
    });
  }
  // Le righe con partite tengono la posizione del backend (che applica i
  // suoi tie-break); le altre si accodano in ordine alfabetico.
  const conPartiteOrdinate = [...conPartite.values()].sort(
    (a, b) => a.posizione - b.posizione,
  );
  const mancanti = squadreIds
    .filter((id) => !conPartite.has(id))
    .sort((a, b) => nomeSquadra(a).localeCompare(nomeSquadra(b), "it"))
    .map((id, i) => rigaVuota(id, conPartiteOrdinate.length + i + 1));
  return [...conPartiteOrdinate, ...mancanti].map((r, i) => ({
    ...r,
    posizione: i + 1,
  }));
}

/** Classifica calcolata dalle partite (usata per i gironi CL): conta solo
 * le partite calcolate e non obsolete, come fa la view. 3 punti a
 * vittoria, 1 a pareggio. Tutte le squadre passate compaiono, anche a zero. */
export function classificaDaPartite(
  squadreIds: string[],
  partite: PartitaItem[],
  nomeSquadra: (id: string) => string,
): RigaClassifica[] {
  const perSquadra = new Map<string, RigaClassifica>();
  for (const id of squadreIds) perSquadra.set(id, rigaVuota(id, 0));

  for (const p of partite) {
    if (p.obsoleto) continue;
    if (p.punteggio_casa === null || p.punteggio_trasferta === null) continue;
    for (const [id, gf, gs, fp, fpSubiti] of [
      [
        p.squadra_casa_id,
        p.gol_casa,
        p.gol_trasferta,
        p.punteggio_casa,
        p.punteggio_trasferta,
      ],
      [
        p.squadra_trasferta_id,
        p.gol_trasferta,
        p.gol_casa,
        p.punteggio_trasferta,
        p.punteggio_casa,
      ],
    ] as Array<[string, number, number, number, number]>) {
      // Squadre fuori dalla lista passata (es. partita di un altro
      // girone) vengono ignorate.
      const r = perSquadra.get(id);
      if (!r) continue;
      r.partite_giocate += 1;
      r.gol_fatti += gf;
      r.gol_subiti += gs;
      r.diff_reti = r.gol_fatti - r.gol_subiti;
      r.fantapunti += Number(fp ?? 0);
      r.fantapunti_subiti += Number(fpSubiti ?? 0);
      r.giocato = true;
      if (gf > gs) {
        r.vinte += 1;
        r.punti += 3;
      } else if (gf === gs) {
        r.pareggiate += 1;
        r.punti += 1;
      } else {
        r.perse += 1;
      }
    }
  }
  return ordina([...perSquadra.values()], nomeSquadra);
}

/** Squadre coinvolte in una competizione, lette dal suo calendario:
 * funziona anche prima che si giochi (le partite esistono già). */
export function squadreDaPartite(partite: PartitaItem[] | undefined): string[] {
  const ids = new Set<string>();
  for (const p of partite ?? []) {
    ids.add(p.squadra_casa_id);
    ids.add(p.squadra_trasferta_id);
  }
  return [...ids];
}

/** Media a partita (fantapunti fatti o subiti); null finche' non si gioca. */
export function mediaAPartita(totale: number, partiteGiocate: number): number | null {
  return partiteGiocate > 0 ? totale / partiteGiocate : null;
}
